import { useEffect, useState, useRef } from "react";
import {
	closestCenter,
	DndContext,
	DragEndEvent,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Accordion, AccordionDetails, Box, Chip, Typography } from "@mui/material";
import { ShimmerTitle } from "react-shimmer-effects";
import ExclamationInCircle from "../../../../assets/svg-fc/ExclamationInCircle";
import AddAndViewAttachments from "../../../../components/reusable-components/add-attachment/AddAndViewAttachments";
import MenuCategorySelector from "../../../../components/reusable-components/menu-category-selector/MenuCategorySelector";
import {
	AiMenuExtractionStatusEnum,
	PdfCompressionQualityEnum,
	SectionTypeEnum,
} from "../../../../constants/enums";
import {
	CORPORATE_QUOTE_PDF_ATTACHMENTS_PATH,
	MAX_DOCUMENT_PAGES,
	MENU_AND_SERVICES_INFO_EXTRACTED_SUCCESS_TEXT,
	NO_MENU_OR_SERVICES_INFO_FOUND_ERROR_TEXT,
	PDF_UPLOAD_ACCEPTED_FORMATS,
	SOMETHING_WENT_WRONG_ERROR_TEXT,
} from "../../../../constants/formConstants";
import IsMobile from "../../../../hooks/isMobile";
import { IAttachmentFile } from "../../../../interfaces/corporateEvent";
import { ICreateQuoteFormSection, IQuoteFormMenuAndServicesInfo } from "../../../../types/quoteFormTypes";
import { menuCategories } from "../../../../utils/quoteFormUtils";
import MenuAndServicesSection from "../menu-and-services-section/MenuAndServicesSection";
import SectionHeader from "../section-header/SectionHeader";
import ConfirmationModal from "../../../../components/reusable-components/modals/confirmation-modal/ConfirmationModal";
import { BUTTON_COLOR } from "../../../../types/coreButtonTypes";
import {
	getAiMenuAndServicesInfoThunk,
	submitMenuExtractionPromptThunk,
	uploadFileToOpenAIThunk,
} from "../../../../store/quote/quoteThunk";
import { useAppDispatch } from "../../../../hooks/storeHooks";
import { useToast } from "../../../../context/ToasterContext";
import UploadPdfCard from "../upload-pdf-card/UploadPdfCard";
import { transformAIResponse } from "../../../../utils/transformers/quoteFormTransformers";
import "./menuAndServicesInfoForm.scss";

let nextSectionClientId = 0;
const createSectionClientId = () => `section-${++nextSectionClientId}`;
const isSameSection = (a: ICreateQuoteFormSection, b?: ICreateQuoteFormSection | null) =>
	a.clientId || b?.clientId ? a.clientId === b?.clientId : a.displayOrder === b?.displayOrder;

interface IMenuAndServicesInfoFormProps {
	menuFormData: IQuoteFormMenuAndServicesInfo;
	servicesInfoSection: ICreateQuoteFormSection;
	selectedFile: File | null;
	isAIExtracting: boolean;
	uploadProgress: number;
	pagesExtracted: number;
	setServicesInfoSection: (data: ICreateQuoteFormSection | null) => void;
	setMenuFormData: (data: IQuoteFormMenuAndServicesInfo) => void;
	setSelectedFile: React.Dispatch<React.SetStateAction<File | null>>;
	setIsAIExtracting: React.Dispatch<React.SetStateAction<boolean>>;
	setUploadProgress: React.Dispatch<React.SetStateAction<number>>;
	setPagesExtracted: React.Dispatch<React.SetStateAction<number>>;
	setShowPhotoLibrary: (sectionIdentifier: number | "services") => void;
}

const MenuAndServicesInfoForm = ({
	menuFormData,
	servicesInfoSection,
	selectedFile,
	isAIExtracting,
	uploadProgress,
	pagesExtracted,
	setServicesInfoSection,
	setMenuFormData,
	setSelectedFile,
	setIsAIExtracting,
	setUploadProgress,
	setPagesExtracted,
	setShowPhotoLibrary,
}: IMenuAndServicesInfoFormProps) => {
	const isMobile = IsMobile();
	const dispatch = useAppDispatch();
	const { showToast } = useToast();
	const [uploadedFile, setUploadedFile] = useState<IAttachmentFile | null>(null);
	const { sections } = menuFormData;
	const [collapseSections, setCollapseSections] = useState<boolean>(false);
	const [collapseServicesInfoSection, setCollapseServicesInfoSection] = useState<boolean>(false);
	const [showDeleteSectionConfirmation, setShowDeleteSectionConfirmation] = useState(false);
	const [showRemoveFileConfirmation, setShowRemoveFileConfirmation] = useState(false);
	const [showCancelUploadSection, setShowCancelUploadSection] = useState(false);
	const [sectionToDelete, setSectionToDelete] = useState<ICreateQuoteFormSection | null>(null);
	const intervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const sectionsMissingClientId = sections?.some((section) => !section.clientId);

	useEffect(() => {
		if (!sectionsMissingClientId) return;
		setMenuFormData({
			...menuFormData,
			sections: sections.map((section) => (section.clientId ? section : { ...section, clientId: createSectionClientId() })),
		});
	}, [sectionsMissingClientId]);

	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	);

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;

		if (active.id !== over?.id) {
			const oldIndex = sections.findIndex((section) => section.clientId === active.id);
			const newIndex = sections.findIndex((section) => section.clientId === over?.id);
			if (oldIndex < 0 || newIndex < 0) return;

			const newSections = arrayMove(sections, oldIndex, newIndex);

			// Update displayOrder for all sections
			const updatedSections = newSections.map((section, index) => ({
				...section,
				displayOrder: index + 1,
			}));

			setMenuFormData({
				...menuFormData,
				sections: updatedSections,
			});
		}
	};

	const handleCategorySelect = (categoryId: string) => {
		if (!servicesInfoSection && sections?.length === 0) {
			setServicesInfoSection({
				sectionTitle: "Services Information",
				sectionType: SectionTypeEnum.SERVICE,
				content: "",
				displayOrder: 11,
				attachments: [],
			});
		}
		const sectionTitle = menuCategories?.find((category) => category?.id === categoryId)?.name as string;
		setMenuFormData({
			...menuFormData,
			sections: [
				...sections,
				{
					sectionTitle,
					sectionType: SectionTypeEnum.MENU,
					content: "",
					displayOrder: sections?.length + 1,
					attachments: [],
					clientId: createSectionClientId(),
				},
			],
		});
	};

	const updateMenuSectionField = (
		section: ICreateQuoteFormSection,
		key: keyof ICreateQuoteFormSection,
		value: string | IAttachmentFile[],
	) => {
		setMenuFormData({
			...menuFormData,
			sections: sections?.map((s) => (isSameSection(s, section) ? { ...s, [key]: value } : s)),
		});
	};

	const uploadFileToOpenAI = async (fileUrl: string) => {
		dispatch(
			uploadFileToOpenAIThunk({
				s3Url: fileUrl,
				compressionLevel: PdfCompressionQualityEnum.EBOOK,
				uploadToOpenAI: true,
			}),
		)
			.unwrap()
			.then((res) => {
				if (res?.openAIResult?.success && res?.openAIResult?.openai_file_id) {
					submitMenuExtractionPrompt(res?.openAIResult?.openai_file_id);
					setUploadProgress((progress) => progress + 9);
				} else {
					onExtractionError(SOMETHING_WENT_WRONG_ERROR_TEXT);
				}
			})
			.catch(onExtractionError);
	};

	const submitMenuExtractionPrompt = async (fileId: string) => {
		dispatch(submitMenuExtractionPromptThunk(fileId))
			.unwrap()
			.then((res) => {
				setUploadProgress((progress) => progress + 5);
				setUploadProgressInterval();
				setTimeout(() => {
					handleGetAiMenuAndServicesInfo(res.id);
				}, 60000);
			})
			.catch(onExtractionError);
	};

	const handleGetAiMenuAndServicesInfo = async (aiBidsExtractionLogId: string) => {
		dispatch(getAiMenuAndServicesInfoThunk(aiBidsExtractionLogId))
			.unwrap()
			.then((res) => {
				if (res.status === AiMenuExtractionStatusEnum.Success) {
					if (res.aiResponse?.error || res.aiResponse?.status_code === 400) {
						onExtractionError(NO_MENU_OR_SERVICES_INFO_FOUND_ERROR_TEXT);
						return;
					}

					const { serviceInfoSection, menuSections } = transformAIResponse(res);

					// Save Service Information to setServicesInfoSection
					if (serviceInfoSection) {
						setServicesInfoSection(serviceInfoSection);
					}
					// Save other sections to menuFormData
					setMenuFormData({
						openAiFileId: res?.fileId,
						aiBidsExtractionLogId: res?.id,
						quoteUrl: uploadedFile?.fileUrl as string,
						sections: menuSections,
					});
					onExtractionSuccess();
				} else if (
					res.status === AiMenuExtractionStatusEnum.Failed ||
					res.status === AiMenuExtractionStatusEnum.RateLimited
				) {
					onExtractionError();
					return;
				} else {
					setTimeout(() => {
						handleGetAiMenuAndServicesInfo(aiBidsExtractionLogId);
					}, 7500);
				}
			})
			.catch(onExtractionError);
	};

	const setUploadProgressInterval = () => {
		intervalRef.current = setInterval(() => {
			setUploadProgress((prevProgress) => {
				if (prevProgress < 91) {
					return prevProgress + 9;
				}
				return prevProgress;
			});
		}, 7500);
	};

	const clearIntervalRef = () => {
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
			intervalRef.current = null;
		}
	};

	const onExtractionError = (message?: string) => {
		showToast(message || SOMETHING_WENT_WRONG_ERROR_TEXT, undefined, "error");
		clearStates();
	};

	const onExtractionSuccess = () => {
		showToast(MENU_AND_SERVICES_INFO_EXTRACTED_SUCCESS_TEXT, undefined, "success");
		setUploadProgress(100);
		setIsAIExtracting(false);
		clearIntervalRef();
	};

	const clearFilesAndProgress = () => {
		setSelectedFile(null);
		setUploadedFile(null);
		setUploadProgress(0);
	};

	const clearStates = () => {
		clearFilesAndProgress();
		clearIntervalRef();
		setIsAIExtracting(false);
	};

	const onCancelUpload = () => {
		clearStates();
		setShowCancelUploadSection(false);
	};

	const onRemoveFile = () => {
		clearFilesAndProgress();
		setMenuFormData({
			...menuFormData,
			openAiFileId: "",
			aiBidsExtractionLogId: "",
			quoteUrl: "",
			sections: [],
		});
		setServicesInfoSection(null);
		setShowRemoveFileConfirmation(false);
	};

	return (
		<Box className="menuAndServicesInfoForm">
			{/* Import Menu Section */}
			<Box className="importMenuSection">
				<Box className="importMenuSectionHeader">
					<Typography variant="h6" className="sectionTitle">
						Upload quote file with menu
					</Typography>
					<Chip label="Beta" size="small" className="betaChip" />
				</Box>

				{selectedFile ? (
					<UploadPdfCard
						pdfFile={selectedFile}
						loading={isAIExtracting}
						progress={uploadProgress}
						onCancelUpload={() => setShowCancelUploadSection(true)}
						onRemoveFile={() => setShowRemoveFileConfirmation(true)}
					/>
				) : (
					<AddAndViewAttachments
						id="pdfFile"
						attachments={uploadedFile ? [uploadedFile] : []}
						attachmentHelperText={isMobile ? "Import pdf" : "Choose a file or drag & drop it here"}
						attachmentTypeText="PDF up to 50MB"
						hideUploadAttachmentsSection={true}
						acceptedFormats={PDF_UPLOAD_ACCEPTED_FORMATS}
						multiple={false}
						maxPages={MAX_DOCUMENT_PAGES}
						remainingPageLimit={MAX_DOCUMENT_PAGES - pagesExtracted}
						directoryPath={CORPORATE_QUOTE_PDF_ATTACHMENTS_PATH}
						handleSetAttachments={(attachments) => {
							setUploadedFile(attachments[0]);
							setUploadProgress((progress) => progress + 5);
							uploadFileToOpenAI(attachments[0].fileUrl);
						}}
						getPageCount={(pageCount) => {
							setPagesExtracted((prevCount: number) => prevCount + pageCount);
						}}
						getFiles={async (files) => {
							setSelectedFile(files?.[0]);
							setIsAIExtracting(true);
						}}
					/>
				)}

				<Box className="betaInfoBanner">
					<ExclamationInCircle />
					<Typography variant="body2" className="infoText">
						This AI-powered feature extracts menu details from your PDF. Since it’s in beta, you’ll need to
						review and correct any inaccuracies before submitting.
					</Typography>
				</Box>
			</Box>

			{/* Separator */}
			<Box className="formSeparator">
				<Typography variant="body2" className="separatorText">
					OR manually add information below
				</Typography>
			</Box>

			{/* Show shimmer loading effect when AI is processing */}
			{isAIExtracting ? (
				<Box className="shimmerContainer">
					<ShimmerTitle line={10} />
				</Box>
			) : (
				<>
					{/* Menu Information Section */}
					{sections?.length > 0 && (
						<Box className="menuInfoSection">
							<SectionHeader
								title="Menu Information"
								collapse={collapseSections}
								collapseMultiple
								setCollapse={setCollapseSections}
							/>

							<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
								<SortableContext
									items={sections.map((section) => section.clientId ?? `order-${section.displayOrder}`)}
									strategy={verticalListSortingStrategy}
								>
									{sections.map((section) => (
										<MenuAndServicesSection
											key={section.clientId ?? `order-${section.displayOrder}`}
											section={section}
											richTextPlaceholder="Enter menu items"
											showHeaderActionButtons={sections?.length > 1}
											isCollapsed={collapseSections}
											onTitleChange={(value) => updateMenuSectionField(section, "sectionTitle", value)}
											onContentChange={(value) => updateMenuSectionField(section, "content", value)}
											setOpenPhotoLibrary={() => setShowPhotoLibrary(section.displayOrder)}
											onDelete={() => {
												setSectionToDelete(section);
												setShowDeleteSectionConfirmation(true);
											}}
											removePhoto={(photoId) => {
												updateMenuSectionField(section, "attachments", section?.attachments?.filter((photo) => photo.id !== photoId));
											}}
										/>
									))}
								</SortableContext>
							</DndContext>
						</Box>
					)}

					{/* Services Information Section */}
					{servicesInfoSection && (
						<Accordion className="servicesInfoSection" expanded={!collapseServicesInfoSection} disableGutters>
							<SectionHeader
								title="Services Information"
								collapse={collapseServicesInfoSection}
								setCollapse={setCollapseServicesInfoSection}
							/>

							<AccordionDetails className="servicesInfoSectionContent">
								<MenuAndServicesSection
									showHeader={false}
									section={servicesInfoSection as ICreateQuoteFormSection}
									richTextPlaceholder="Enter service information"
									setOpenPhotoLibrary={() => setShowPhotoLibrary("services")}
									onContentChange={(value) => {
										setServicesInfoSection({
											...(servicesInfoSection as ICreateQuoteFormSection),
											content: value,
										});
									}}
									removePhoto={(photoId) => {
										setServicesInfoSection({
											...(servicesInfoSection as ICreateQuoteFormSection),
											attachments: servicesInfoSection?.attachments?.filter((photo) => photo.id !== photoId),
										});
									}}
								/>
							</AccordionDetails>
						</Accordion>
					)}

					{/* Menu category selector */}
					<MenuCategorySelector totalSections={sections?.length} onCategorySelect={handleCategorySelect} />
				</>
			)}

			<ConfirmationModal
				open={showDeleteSectionConfirmation}
				heading="Delete section"
				description="Are you sure you want to delete this section? This action cannot be undone,"
				buttonText="Confirm"
				buttonColor={BUTTON_COLOR.ERROR}
				onClose={() => {
					setShowDeleteSectionConfirmation(false);
					setSectionToDelete(null);
				}}
				onClickSave={() => {
					setMenuFormData({
						...menuFormData,
						sections: sections
							?.filter((s) => !isSameSection(s, sectionToDelete))
							.map((s, index) => ({ ...s, displayOrder: index + 1 })),
					});
					setShowDeleteSectionConfirmation(false);
					setSectionToDelete(null);
				}}
			/>

			<ConfirmationModal
				open={showRemoveFileConfirmation}
				heading="Remove file"
				description="Are you sure you want to remove this file, removing it will clear all added data?"
				buttonText="Confirm"
				buttonColor={BUTTON_COLOR.ERROR}
				onClose={() => setShowRemoveFileConfirmation(false)}
				onClickSave={onRemoveFile}
			/>

			<ConfirmationModal
				open={showCancelUploadSection}
				heading="Cancel upload"
				description="Are you sure you want to cancel this upload, all your progress will be lost?"
				buttonText="Confirm"
				buttonColor={BUTTON_COLOR.ERROR}
				onClose={() => setShowCancelUploadSection(false)}
				onClickSave={onCancelUpload}
			/>
		</Box>
	);
};

export default MenuAndServicesInfoForm;
